import React, {
  ChangeEvent,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import * as XLSX from "xlsx";
import { uploadToFirestore } from "../../services/FirestoreUpload";
import { getAuth } from "firebase/auth";
import "./ExcelImportButton.css";
import { IonButton, IonToast } from "@ionic/react";

export interface ExcelImportButtonHandle {
  open: () => void;
}

interface ExcelImportButtonProps {
  dealerId: string;
  compact?: boolean; // when true render a simple inline label suitable for ListItemText
}

// Update the component to accept props and forward a handle
const ExcelImportButton = forwardRef<
  ExcelImportButtonHandle,
  ExcelImportButtonProps
>(({ dealerId, compact = true }, ref) => {
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [rowData, setRowData] = useState<
    Array<{ itemNumber: string; quantity: string; description: string }>
  >([]);
  const [isUploaded, setIsUploaded] = useState<boolean>(false);
  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        fileInputRef.current?.click();
      },
    }),
    []
  );

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    setIsUploaded(false);
    setIsFileSelected(false);
    const file = event.target.files?.[0];
    if (!file) return;

    setIsFileSelected(true);

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, {
        type: file.name.endsWith(".xls") ? "binary" : "array",
      });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      const orderNumberCell = worksheet["E2"];
      if (orderNumberCell && orderNumberCell.v) {
        const orderNumberValue = orderNumberCell.v.toString();
        const parsedOrderNumber = orderNumberValue.replace("Order #: ", "");
        setOrderNumber(parsedOrderNumber);
      } else {
        setOrderNumber(null);
      }

      const extractedData = [];
      let row = 7;
      while (worksheet[`C${row}`]) {
        let quantity = "N/A";
        if (worksheet[`D${row}`]) {
          quantity = worksheet[`D${row}`].v;
        } else if (worksheet[`E${row}`]) {
          const mergedCells = worksheet["!merges"];
          const mergeRange = mergedCells?.find(
            (m) => m.s.c <= 4 && m.e.c >= 4 && m.s.r <= row && m.e.r >= row
          );
          if (mergeRange) {
            quantity =
              worksheet[
                XLSX.utils.encode_cell({ c: mergeRange.s.c, r: mergeRange.s.r })
              ]?.v || "N/A";
          }
        }

        extractedData.push({
          itemNumber: worksheet[`C${row}`].v,
          quantity: quantity,
          description: worksheet[`F${row}`]?.v || "N/A",
        });
        row++;
      }

      setRowData(extractedData);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleUploadToFirestore = async () => {
    const auth = getAuth();
    const userId = auth.currentUser ? auth.currentUser.uid : "Unknown";
    let uploadSuccessful = false;

    if (orderNumber && rowData.length > 0) {
      for (const row of rowData) {
        let paddedItemNumber = row.itemNumber.toString().padStart(3, "0");
        const docName = `${orderNumber}${paddedItemNumber}`;

        const success = await uploadToFirestore("cabinetLog", docName, {
          orderNumber,
          itemNumber: row.itemNumber,
          quantity: row.quantity,
          description: row.description,
          scanStatus: "Not Yet Scanned",
          uploadedBy: userId,
          cabinetLineId: "BFOjR60OTxkix8Sg7NCn",
          dealerId: dealerId,
          jobName: "",
          estimatedDeliveryDate: "",
        });
        if (success) uploadSuccessful = true;
      }
      setIsUploaded(true);
      if (uploadSuccessful) setShowToast(true);
    }
  };

  // Compact mode: render a simple inline label suitable for ListItemText.
  if (compact) {
    const label =
      isFileSelected || isUploaded
        ? "Attach A Different Report"
        : "Attach Cabinet Report";
    return (
      <>
        <span
          style={{ cursor: "pointer", userSelect: "none" }}
          onClick={() => fileInputRef.current?.click()}
        >
          {label}
        </span>
        <input
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          ref={fileInputRef}
          style={{ display: "none" }}
        />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message="Successfully uploaded to database. View in cabinet log or scan on mobile."
          duration={2500}
          position="top"
          cssClass="safe-area-toast-top"
        />
      </>
    );
  }

  // Full mode: original UI with IonButtons
  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "20px",
        }}
      >
        <div className="buttons-container" style={{ marginBottom: "10px" }}>
          {rowData.length > 0 && (
            <IonButton onClick={handleUploadToFirestore}>
              Looks good, upload to database
            </IonButton>
          )}
          <IonButton
            onClick={() => fileInputRef.current?.click()}
            style={{ marginRight: "10px" }}
          >
            {isFileSelected || isUploaded
              ? "Attach A Different Report"
              : "Attach Cabinet Report"}
          </IonButton>
        </div>
        <div>
          <input placeholder="Job Name" style={{ display: "none" }} />
          <input
            type="date"
            placeholder="Estimated Delivery Date"
            style={{ display: "none" }}
          />
        </div>
      </div>
      {orderNumber && <p>Order Number: {orderNumber}</p>}
      <div>
        {rowData.map((row, index) => (
          <div key={index} style={{ marginBottom: "10px" }}>
            <p>Item Number: {row.itemNumber}</p>
            <p>Quantity: {row.quantity}</p>
            <p>Description: {row.description}</p>
          </div>
        ))}
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        onChange={handleFileUpload}
        ref={fileInputRef}
        style={{ display: "none" }}
      />
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message="Successfully uploaded to database. View in cabinet log or scan on mobile."
        duration={2500}
        position="top"
        cssClass="safe-area-toast-top"
      />
    </>
  );
});

export default ExcelImportButton;
